/**
 * The registry's definition, as the taxonomy and flows screens edit it - ported from Veramenta's
 * back office (lib/registry.ts). Only the definition half is here: GeoNexora's admin reads the
 * operator definition, never a company's resolved types, so the card-drawing helpers were left out.
 */
import type { Locale } from './i18n';

export type FieldType =
  | 'text' | 'integer' | 'decimal' | 'money' | 'quantity' | 'boolean'
  | 'date' | 'timestamp' | 'choice' | 'reference' | 'json' | 'file'
  /** A composite value of a value type: an address, a period (docs/en/43 sec.5). */
  | 'value'
  /** A shape on a map, as GeoJSON in longitude and latitude (docs/en/60 sec.3). */
  | 'geometry';

export type FieldClass = 'none' | 'money' | 'contact' | 'pii' | 'premium';
export type FieldRole = 'value' | 'due' | 'title' | 'shape'
  | 'customer' | 'contact' | 'bill-to' | 'ship-to' | 'site' | 'end-user';

/** The parts a relation plays in a piece of work (docs/en/41 sec.5): each played by a reference. */
export const PARTY_ROLES: FieldRole[] = ['customer', 'contact', 'bill-to', 'ship-to', 'site', 'end-user'];

export interface ChoiceView { key: string; labels?: Record<string, string> }

/** What a person sees a field called: their language, then English, then Dutch, then its key. */
export function labelOf(labels: Record<string, string> | undefined, locale: Locale, fallback: string): string {
  return labels?.[locale] || labels?.en || labels?.nl || fallback;
}

/** The places a type is drawn (docs/en/42 sec.6.1). */
export type ViewKind = 'tile' | 'detail' | 'list' | 'form';
export type ButtonAction = 'open' | 'edit' | 'archive' | 'issue' | 'create-linked' | 'move' | 'run';

/** The card every CRM board started with: kept only so that copied defaults read. */
export const PLAIN_CARD = 'crm.deal';

// ------------------------------------------------------------------ the definition

/** A field as the definition writes it: what the Types screen edits. */
export interface FieldDefinition {
  key: string;
  type: FieldType;
  required?: boolean;
  labels?: Record<string, string>;
  column?: string;
  filterable?: boolean;
  fieldClass?: FieldClass;
  defaultValue?: string;
  refersTo?: string;
  choices?: (string | ChoiceView)[];
  was?: string;
  unit?: string;
  many?: boolean;
  role?: FieldRole;
  identity?: 'strong' | 'weak';
  normalise?: string;
  shows?: string[];
  /** For a value field: the value type it holds (docs/en/43 sec.5). */
  of?: string;
}

/** A value type: a composite with no identity, held by the fields of templates (docs/en/43 sec.5). */
export interface ValueTypeDefinition { key: string; labels?: Record<string, string>; fields?: FieldDefinition[] }

export interface TypeDefinition {
  key: string;
  root?: string;
  parent?: string;
  abstract?: boolean;
  binding?: string;
  table?: string;
  labels?: Record<string, string>;
  fields?: FieldDefinition[];
  capabilities?: string[];
  actions?: { key: string; description?: Record<string, string> }[];
  guards?: unknown[];
  expects?: FieldRole[];
  /** The states its cards go through, written in (docs/en/39); a type now names a flow instead. */
  lifecycle?: LifecycleDefinition;
  /** The flow its cards go through, by key (docs/en/40). */
  flow?: string;
  /** The roles every lifecycle below it must have a state for. */
  expectsStates?: StateRole[];
  [other: string]: unknown;
}

export type StateRole = 'start' | 'won' | 'lost' | 'done';
export interface StateDefinition { key: string; labels?: Record<string, string>; colour?: string; roles?: StateRole[] }
export interface LifecycleDefinition { states: StateDefinition[] }

export interface RegistryDefinition {
  /** The registry's actions (docs/en/47 §4): kept as they are written, edited as directives. */
  actions?: { key: string; target?: string; labels?: Record<string, string>; [part: string]: unknown }[];
  format: number; types: TypeDefinition[]; links?: LinkDefinition[]; flows?: FlowDefinition[];
  views?: ViewDefinition[]; menu?: MenuDefinition[]; mappings?: MappingDefinition[]; [other: string]: unknown
}

/** A kind of link as it is written (docs/en/32 sec.4, docs/en/42 sec.4.4). */
export interface LinkDefinition {
  key: string; from: string; to: string; cardinality?: string; inverse?: string; dated?: boolean;
  labels?: Record<string, string>; fields?: FieldDefinition[];
}
export interface SectionDefinition { key: string; labels?: Record<string, string>; items: string[] }
export interface ButtonDefinition {
  key: string; labels?: Record<string, string>; does: ButtonAction; link?: string; type?: string; state?: string; style?: string;
  action?: string;
}
export interface ViewDefinition {
  key: string; type: string; kind: ViewKind; labels?: Record<string, string>; sections: SectionDefinition[];
  buttons?: ButtonDefinition[];
}
export interface MenuDefinition {
  key: string; labels?: Record<string, string>; group: string; order?: number; opens: string; needs?: string[];
}
export type Transform = 'as-is' | 'trim' | 'upper' | 'lower' | 'number' | 'number-nl' | 'date' | 'boolean' | 'choice' | 'constant';
export interface ColumnDefinition {
  from?: string; to: string; transform?: Transform; pattern?: string; values?: Record<string, string>; constant?: string;
}
export interface ReferenceDefinition { from: string; link: string; type: string; by?: string }
/** A mapping: data shaped like this becomes instances of that type, so (docs/en/42 sec.8.3). */
export interface MappingDefinition {
  key: string; labels?: Record<string, string>; target: string; source?: string; keyColumn?: string; matchOn?: string[];
  columns: ColumnDefinition[]; references?: ReferenceDefinition[]; onExisting?: 'update' | 'skip';
}

/** A flow: the states work goes through, a Process directive that card types name (docs/en/40). */
export interface FlowDefinition { key: string; labels?: Record<string, string>; states: StateDefinition[];
  transitions?: TransitionDefinition[] }

/** A move a flow allows (docs/en/47 §3): from states or roles (`@lost`, `*`), to one, perhaps under a condition. */
export interface TransitionDefinition {
  from: string[];
  to: string;
  guard?: string;
  requires?: string[];
  permission?: string;
  refusal?: Record<string, string>;
}

export interface ChangeView { kind: 'adds' | 'compatible' | 'breaks'; where: string; type?: string; member?: string; what: string }
export interface ProblemView { code: string; where: string; message: string }

export interface DraftView {
  valid: boolean;
  contentHash: string;
  kind: 'adds' | 'compatible' | 'breaks';
  unchanged: boolean;
  publishable: boolean;
  changes: ChangeView[];
  blockedBy: ChangeView[];
  savedBy?: string;
  savedAt?: string;
}

export interface CurrentDefinition { version: number; contentHash: string; publishedBy?: string; publishedAt?: string; definition: RegistryDefinition }

/** A choice as the definition may write it - a plain key, as the first definitions did, or with labels. */
export function choiceOf(choice: string | ChoiceView): ChoiceView {
  return typeof choice === 'string' ? { key: choice, labels: {} } : { key: choice.key, labels: choice.labels ?? {} };
}

/** The shapes the server refuses keys in, checked here first so the screen can say so as it is typed. */
export const TYPE_KEY = /^[a-z][a-z0-9_-]*(\.[a-z][a-z0-9_-]*)*$/;
export const FIELD_KEY = /^[a-z][A-Za-z0-9]*$/;
export const CHOICE_KEY = /^[a-z0-9][a-z0-9-]*$/;

/**
 * The card types a definition holds - `crm.deal` and the types below it that are not
 * abstract - for a screen of the platform's, which reads the definition rather than the
 * types (an operator is in no company, and the types endpoint is a company's).
 */
export function cardTypesOf(definition: RegistryDefinition | null | undefined): TypeDefinition[] {
  const types = definition?.types ?? [];
  const byKey = new Map(types.map((type) => [type.key, type]));
  const isCard = (key: string) => {
    const seen = new Set<string>();
    for (let at: string | undefined = key; at && !seen.has(at); at = byKey.get(at)?.parent) {
      seen.add(at);
      if (at === PLAIN_CARD) return true;
    }
    return false;
  };
  return types.filter((type) => !type.abstract && isCard(type.key));
}

// ------------------------------------------------------------------ the hierarchy

/** A type's chain, from itself up to its root: `ict.card`, `crm.deal`, `board.card`, `workitem`. */
export function lineageOf(definition: RegistryDefinition | null | undefined, key: string): TypeDefinition[] {
  const byKey = new Map((definition?.types ?? []).map((type) => [type.key, type]));
  const out: TypeDefinition[] = [];
  const seen = new Set<string>();
  for (let at: string | undefined = key; at && !seen.has(at); at = byKey.get(at)?.parent) {
    seen.add(at);
    const type = byKey.get(at);
    if (!type) break;
    out.push(type);
  }
  return out;
}

/** One field as a type has it, and where it comes from. */
export interface FieldOrigin {
  field: FieldDefinition;
  /** The type furthest up that declares it. */
  from: string;
  /** Types below that declare it again to narrow it or to keep it in a column, nearest last. */
  narrowedBy: string[];
}

/**
 * Every field a type has, grouped by the type it comes from, top first (docs/en/38): what
 * the work item gives, then the board card, then the CRM card, then the type's own. A field
 * declared again lower down - the stage, which the CRM card keeps in a column - stays under
 * the type that introduced it and says who narrowed it. What it shows is the nearest
 * declaration, which is what the type has.
 */
export function fieldsByOrigin(definition: RegistryDefinition | null | undefined, key: string): { type: TypeDefinition; fields: FieldOrigin[] }[] {
  const chain = lineageOf(definition, key).reverse();
  const groups = chain.map((type) => ({ type, fields: [] as FieldOrigin[] }));
  const placed = new Map<string, FieldOrigin>();
  chain.forEach((type, depth) => {
    for (const field of type.fields ?? []) {
      const known = placed.get(field.key);
      if (known) {
        known.field = field;
        known.narrowedBy.push(type.key);
      } else {
        const origin = { field, from: type.key, narrowedBy: [] as string[] };
        placed.set(field.key, origin);
        groups[depth].fields.push(origin);
      }
    }
  });
  return groups;
}

/** A node of the type tree: a type and the types that extend it. */
export interface TypeNode { type: TypeDefinition; children: TypeNode[] }

/** The types below (and including) `top`, as a tree in the definition's order. */
export function typeTree(definition: RegistryDefinition | null | undefined, top: string): TypeNode | null {
  const types = definition?.types ?? [];
  const root = types.find((type) => type.key === top);
  if (!root) return null;
  const build = (type: TypeDefinition, seen: Set<string>): TypeNode => ({
    type,
    children: types.filter((child) => child.parent === type.key && !seen.has(child.key))
      .map((child) => build(child, new Set([...seen, child.key])))
  });
  return build(root, new Set([top]));
}

/** The roles a type and those above it expect of every type below with instances. */
export function expectedRoles(definition: RegistryDefinition | null | undefined, key: string): FieldRole[] {
  const roles = new Set<FieldRole>();
  for (const type of lineageOf(definition, key)) (type.expects ?? []).forEach((role) => roles.add(role));
  return [...roles];
}

// ------------------------------------------------------------------ lifecycles

/**
 * The states a type goes through, the type that says so, and the flow they are, if they are
 * one (docs/en/39, docs/en/40): the flow it names, or the states it writes, or the nearest
 * ancestor's.
 */
export function lifecycleOf(definition: RegistryDefinition | null | undefined, key: string):
  { from: string; flow?: FlowDefinition; lifecycle: LifecycleDefinition } | null {
  for (const type of lineageOf(definition, key)) {
    if (type.flow) {
      const flow = (definition?.flows ?? []).find((f) => f.key === type.flow);
      return flow ? { from: type.key, flow, lifecycle: { states: flow.states } } : null;
    }
    if (type.lifecycle) return { from: type.key, lifecycle: type.lifecycle };
  }
  return null;
}

/** The card types that go through a flow, naming it or taking it from a parent that does. */
export function typesUsingFlow(definition: RegistryDefinition | null | undefined, flowKey: string): TypeDefinition[] {
  // GeoNexora has no CRM card: any template that is not abstract may go through a flow.
  return (definition?.types ?? []).filter((type) => !type.abstract).filter((type) => lifecycleOf(definition, type.key)?.flow?.key === flowKey);
}

/** A key for a new flow, from what it is for: unique among the flows and types. */
export function newFlowKey(definition: RegistryDefinition | null | undefined, base: string): string {
  const stem = `flow.${base.toLowerCase().replace(/^flow\./, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'new'}`;
  const taken = new Set([...(definition?.flows ?? []).map((f) => f.key), ...(definition?.types ?? []).map((t) => t.key)]);
  let key = stem;
  for (let n = 2; taken.has(key); n++) key = `${stem}-${n}`;
  return key;
}

/** The roles a type's lifecycle must play: what it and the types above it expect. */
export function expectedStatesOf(definition: RegistryDefinition | null | undefined, key: string): StateRole[] {
  const roles = new Set<StateRole>();
  for (const type of lineageOf(definition, key)) (type.expectsStates ?? []).forEach((role) => roles.add(role));
  // An end that is won or lost is an end: "and an end" says nothing more then.
  if (roles.has('won') || roles.has('lost')) roles.delete('done');
  const order: StateRole[] = ['start', 'won', 'lost', 'done'];
  return order.filter((role) => roles.has(role));
}

export const STATE_KEY = /^[a-z0-9][a-z0-9_-]{0,39}$/;
